import {
  Box,
  Grid,
  Collapse,
  Popover,
  useTheme,
  useMediaQuery,
  Link,
  Menu,
  MenuItem,
  IconButton,
  ListItemIcon,
  Typography,
  Alert,
  AlertTitle,
} from "@mui/material";
import Header from "../../common/Header";
import CustomTextField from "../../common/CustomTextField";
import {
  ModeOutlined,
  SearchOutlined,
  SettingsAccessibility,
} from "@mui/icons-material";
import Card from "../../common/Card";
import { useForm } from "react-hook-form";
import Button from "../../common/Button";
import Table from "../../common/CustomTable";
import LegacyToolBar from "../../common/LegacyToolBar";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AppConfigInfo,
  fetchAppConfigList,
  useCommonReducer,
} from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { tokens } from "../../../theme";
import UpdateAppConfigPopup from "../../common/UpdateAppConfigPopup";
import { useNavigation } from "../../../hooks/useNavigation";
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import { useModalStyles } from "../../common/useModalStyles";

interface Column {
  id: string;
  label: string | JSX.Element;
  minWidth?: number;
  width?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
  padding?: string;
}

const ConfigList = () => {
  const ITEMS_PER_PAGE = 10;
  const { control, handleSubmit, setValue, reset } = useForm<any>();
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { primaryBtnSx, filterToggleBtnSx, tokens: modalTokens } = useModalStyles();

  const legacyItems = [{ icon: <ModeOutlined color="info" />, text: "Edit" }];
  const [appConfigData, setAppConfigData] = useState<any>({
    appconfigId: "",
    configValue: "",
  });
  const [isEditing, setIsEditing] = useState(false); // State to control editing mode
  const { appConfigList } = useCommonReducer();
  const [showUpdateConfigPopup, setUpdateConfigPopup] = useState(false);
  const { navigate } = useNavigation();
  const [originalConfigValue, setOriginalConfigValue] = useState<any>();
  const [showCard, setShowCard] = useState(false);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [searchCriteria, setSearchCriteria] = useState<any>({
      configName: "",
      productCategories: [],
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
  });

  useEffect(() => {
    dispatch(fetchAppConfigList());
  }, []);

  useEffect(() => {
    if (isEditing && appConfigData.configValue !== undefined) {
      setValue("configValue", appConfigData.configValue);
    }
  }, [isEditing, appConfigData.configValue, setValue]);
  
  const patientPrimaryBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };
    // Add this to match the Search button accent color
  const configTextFieldSx = {
    "& .MuiOutlinedInput-root, & .MuiOutlinedInput-root.Mui-focused": {
      "& fieldset": {
        borderColor: "#00B8D9 !important",   // Same color as Search button
      },
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: "0 0 0 3px rgba(0, 184, 217, 0.2) !important",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#00B8D9 !important",
    },
  };

const configCardSx = {
  borderRadius: "14px",
  border: `1px solid ${isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)"}`,
  backgroundColor: isDark ? "#141720" : "rgba(255,255,255,0.82)",
  backgroundImage: "none",
  backdropFilter: "blur(8px)",
  boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
};

const configTableSx = {
  "& .MuiTableContainer-root": {
    borderRadius: "12px",
    border: "none",
    backgroundColor: "transparent",
  },
  "& table": {
    
    borderCollapse: "collapse",
    borderSpacing: 0,
  },
  "& .MuiTableCell-root": { 
    borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`, 
    py: 2.15,                    
    fontSize: "0.84rem",
    verticalAlign: "top",
  },
  "& .config-value-cell": {
    maxHeight: "200px",
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: "12px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    lineHeight: 1.55,
    scrollbarWidth: "thin",
    scrollbarColor: isDark
      ? "#475569 rgba(15,23,42,0.6)"
      : "#94a3b8 rgba(241,245,249,0.8)",
    "&::-webkit-scrollbar": {
      width: "6px",
      height: "6px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
      borderRadius: "20px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: isDark ? "#475569" : "#94a3b8",
      borderRadius: "20px",
      border: "1px solid transparent",
      backgroundClip: "padding-box",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#64748b",
    },
  },
  "& .MuiTableHead-root .MuiTableCell-root": {
    height: "42px",
  },
  "& .MuiTableHead-root .MuiTypography-root": {
    color: "#00B8D9 !important",
    fontSize: "0.78rem",
    fontWeight: 800,
    letterSpacing: "0.02em",
  },
  "& .MuiTableBody-root .MuiTableRow-root": {
    transition: "background-color 0.18s ease, box-shadow 0.18s ease",
  },
  "& .MuiTableBody-root .MuiTableRow-root:hover": {
    backgroundColor: `${isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)"} !important`,
  },
  "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableBody-root .MuiTypography-root": {
    color: modalTokens.textPrimary,
    fontSize: "0.84rem",
  },
  "& .MuiIconButton-root": {
    color: isDark ? "#94a3b8" : "#475569",
    borderRadius: "8px",
    "&:hover": {
      color: modalTokens.accent,
      backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)",
    },
  },
  "& .MuiPaginationItem-root": {
    borderColor: isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)",
    color: modalTokens.textSecondary,
    borderRadius: "8px",
    "&.Mui-selected": {
      color: "#fff",
      backgroundColor: modalTokens.accent,
      borderColor: modalTokens.accent,
    },
  },
};

  const filterPanelSx = {
    ...configCardSx,
    p: { xs: "14px !important", md: "18px !important" },
  };

  const configColumns: Column[] = [
    {
      id: "configName",
      label: "Config Name",
      minWidth: 180,
      width: 280,
      align: "left",
    },
    {
      id: "configValue",
      label: "Config Value",
      minWidth: 400,
      width: 650,
      align: "left",
    },
  ];

  const cancelEdit = () => {
    setIsEditing(false);
    setAppConfigData({ appconfigId: "", configValue: "" });
    setOriginalConfigValue(undefined);
    reset({ configValue: "" });
  };

  const renderTextfieldComponent = (configValue: string, appconfigId: string | number) => {
    if (isEditing && appConfigData.appconfigId === appconfigId) {
      return (
        <CustomTextField
          controllerName="configValue"
          autoFocus
          control={control}
          onChange={(event) => {
            setAppConfigData((prev: any) => ({
              ...prev,
              configValue: event.target.value,
            }));
          }}
          onBlur={() => {
            const newValue = appConfigData.configValue?.trim();
            const oldValue = originalConfigValue?.trim();

            if (newValue !== oldValue && newValue !== undefined) {
              setUpdateConfigPopup(true);
            } else {
              cancelEdit();
            }
          }}
          name="configValue"
          sx={configTextFieldSx}
        />
      );
    }

    return (
      <Box
        className="config-value-cell"
        sx={{ cursor: "pointer" }}
        onClick={() => {
          const currentValue = configValue || "";
          setAppConfigData({
            appconfigId: appconfigId,
            configValue: currentValue,
          });
          setOriginalConfigValue(currentValue);
          setIsEditing(true);
          setValue("configValue", currentValue);
        }}
      >
        {configValue?.length > 180
          ? `${configValue.slice(0, 180)}... (click to edit)`
          : configValue}
      </Box>
    );
  };

  // CS-1254 Add Filter Functionality in Config List
  const onSubmit = (data: any) => {
    const data2: any = {
      configName: data.configName ? data.configName : "",
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
    };
    setSearchCriteria(data2);
    navigate("/configuration", { state: { productsListSearchCriteria: data2 } });
  };

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      currentPage: pageNumber + 1,
      itemsPerPage: ITEMS_PER_PAGE,
    };
    setSearchCriteria(data);
    navigate("/configuration", { state: { productsListSearchCriteria: data } });
  };

  const configListData: any = useMemo(() => {
    let listData: any = [];
    if (appConfigList) {
      // Filter by configName if provided (case-insensitive, contains)
      let filteredList = appConfigList;
      if (searchCriteria.configName && searchCriteria.configName.trim() !== "") {
        const searchValue = searchCriteria.configName.trim().toLowerCase();
        filteredList = appConfigList.filter((data: AppConfigInfo) =>
          data.configName?.toLowerCase().includes(searchValue)
        );
      }
      const startIdx = ((searchCriteria.currentPage || 1) - 1) * ITEMS_PER_PAGE;
      const endIdx = startIdx + ITEMS_PER_PAGE;
      const paginatedList = filteredList.slice(startIdx, endIdx);

      listData = paginatedList.map((data: AppConfigInfo) => ({
        appConfigId: data.appconfigId,
        configName: data.configName,
        configValue:
          appConfigData.appconfigId === data.appconfigId ? (
            renderTextfieldComponent(data.configValue, data.appconfigId)
          ) : (
            <Box
              className="config-value-cell"
              sx={{ cursor: "pointer" }}
              onClick={() => {
                const currentValue = data.configValue || "";
                setAppConfigData({
                  appconfigId: data.appconfigId,
                  configValue: currentValue,
                });
                setOriginalConfigValue(currentValue);
                setIsEditing(true);
                setValue("configValue", currentValue);
              }}
            >
              {data.configValue?.length > 180
                ? `${data.configValue.slice(0, 180)}... (click to edit)`
                : data.configValue}
            </Box>
          ),
      }));
    }
    return listData;
  }, [appConfigList, appConfigData, isEditing, searchCriteria.configName, searchCriteria.currentPage]);
  //
  return (
    <>
      {showUpdateConfigPopup && (
        <UpdateAppConfigPopup
          open={showUpdateConfigPopup}
          appConfigData={appConfigData}
          originalConfigValue={originalConfigValue}
          onClose={() => {
            setUpdateConfigPopup(false);
            setIsEditing(false);
            setAppConfigData({ appconfigId: "", configValue: "" });
          }}
        />
      )}

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, md: 3 },
          py: 2,
        }}
      >
        {/* Configuration Notice */}
        <Box
          sx={{
            p: 2,
            mb: 2,
            borderRadius: "14px",
            border: `2px solid ${colors.error[500]}`,
            backgroundColor: colors.grey[800],
          }}
        >
          <Typography variant="h6" fontWeight="bold" color={colors.info[300]} gutterBottom>
            Configuration Notice
          </Typography>
          <Typography variant="body2" color={colors.error[400]}>
            To change configuration, please click on <strong style={{ color: colors.error[300] }}>"Config Value"</strong>.
            Please be sure while editing these values as it will affect the entire application instantly.
          </Typography>
        </Box>

        {/* Filters */}
        <Box>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={filterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item md={4} xs={12}>
                    <CustomTextField
                      controllerName="configName"
                      control={control}
                      label="Config Name"
                      placeholder="Enter config name"
                      size="small"
                      sx={configTextFieldSx}
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<SearchOutlined />}
                      sx={{ ...patientPrimaryBtnSx, height: "42px", py: 0 }}
                      type="submit"
                      removeUpperCase
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>

        {/* Main Config List Card */}
        <Box mt={2}>
          <Card sx={{ ...configCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
              <Grid container justifyContent="space-between" alignItems="center" spacing={1.2}>
                <Grid item xs={12} md>
                  <Typography variant='h3' fontWeight={800} color="text.primary">Config List</Typography>
                </Grid>
                <Grid item xs={12} md="auto">
                  <Button variant="outlined" color="info" onClick={toggleFilterPanel} startIcon={<SearchOutlined />} fullWidth sx={filterToggleBtnSx} removeUpperCase>
                   {showCard ? "Hide Filters" : "Show Filters"}
                  </Button>
                </Grid>
              </Grid>

              <Table
                columns={configColumns}
                rows={configListData || []}
                emptyText="No Configuration(s) Found"
                showMenu={true}
                sx={configTableSx}
                totalPages={Math.ceil(
                  (appConfigList
                    ? (
                        searchCriteria.configName && searchCriteria.configName.trim() !== ""
                          ? appConfigList.filter((data: AppConfigInfo) =>
                              data.configName?.toLowerCase().includes(searchCriteria.configName.trim().toLowerCase())
                            )
                          : appConfigList
                      ).length
                    : 0
                  ) / ITEMS_PER_PAGE
                )}
                pageNumber={handlePagination}
                currentPageNumber={(searchCriteria.currentPage || 1) - 1}
                totalElementText={
                  "Records: " +
                  (appConfigList
                    ? (
                        searchCriteria.configName && searchCriteria.configName.trim() !== ""
                          ? appConfigList.filter((data: AppConfigInfo) =>
                              data.configName?.toLowerCase().includes(searchCriteria.configName.trim().toLowerCase())
                            )
                          : appConfigList
                      ).length
                    : 0)
                }
              />
          </Card>
        </Box>
      </Box>
    </>
  );
};

export default ConfigList;
